import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';

const fmtN = (n,dp=2) => Number(n||0).toLocaleString(undefined,{maximumFractionDigits:dp});
const blank = () => ({ date:new Date().toISOString().split('T')[0], export_cusdec:'', client:'', inv_value_usd:'', export_inv_no:'', cusdec_no:'', hs_code:'', quantity:'', units:'', liability:'' });

export default function IssueGoodsPage() {
  const toast = useToast();
  const [form, setForm] = useState(blank());
  const [tiep, setTiep] = useState([]);
  const [issues, setIssues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedTiep, setSelectedTiep] = useState(null);

  const loadTiep   = useCallback(() => api.get('/tiep_goods/list.php').then(r => { if(r.data.success) setTiep(r.data.data); }), []);
  const loadIssues = useCallback(() => api.get('/issue_goods/list.php').then(r => { if(r.data.success) setIssues(r.data.data); }), []);

  useEffect(() => { loadTiep(); loadIssues(); }, [loadTiep, loadIssues]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleCusdec = cusdec => {
    const entry = tiep.find(t => t.cusdec_no === cusdec);
    setSelectedTiep(entry || null);
    setForm(f => ({ ...f, cusdec_no: cusdec, hs_code: entry?.hs_code||'', units: entry?.units||'', quantity:'', liability:'' }));
  };

  const handleQty = qty => {
    const entry = selectedTiep || tiep.find(t => t.cusdec_no === form.cusdec_no);
    if (entry && +qty > 0) {
      const liab = ((+entry.liability / +entry.qty) * +qty).toFixed(2);
      setForm(f => ({ ...f, quantity: qty, liability: liab }));
    } else { setForm(f => ({ ...f, quantity: qty })); }
  };

  const handleSave = async () => {
    const req = ['date','export_cusdec','client','export_inv_no','cusdec_no','hs_code','quantity','units'];
    if (req.some(k => !form[k])) { toast('Please fill all required fields', 'error'); return; }
    setSaving(true);
    try {
      const url = editId ? '/issue_goods/update.php' : '/issue_goods/create.php';
      const payload = { ...form, id: editId, inv_value_usd: +form.inv_value_usd||0, quantity: +form.quantity, liability: +form.liability||0 };
      const { data } = await api.post(url, payload);
      if (data.success) {
        toast(editId ? 'Record updated!' : 'Issue record saved!');
        setForm(blank()); setEditId(null); setSelectedTiep(null);
        loadIssues(); loadTiep();
      } else toast(data.error||'Save failed', 'error');
    } catch { toast('Network error', 'error'); }
    finally { setSaving(false); }
  };

  const handleEdit = rec => {
    const entry = tiep.find(t => t.cusdec_no === rec.cusdec_no);
    setSelectedTiep(entry||null);
    setForm({ date:rec.date?.split('T')[0]||'', export_cusdec:rec.export_cusdec, client:rec.client, inv_value_usd:String(rec.inv_value_usd||''), export_inv_no:rec.export_inv_no, cusdec_no:rec.cusdec_no, hs_code:rec.hs_code, quantity:String(rec.quantity), units:rec.units, liability:String(rec.liability) });
    setEditId(rec.id);
    window.scrollTo({ top:0, behavior:'smooth' });
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this record?')) return;
    const { data } = await api.post('/issue_goods/delete.php', { id });
    if (data.success) { toast('Deleted'); loadIssues(); loadTiep(); }
    else toast(data.error||'Delete failed', 'error');
  };

  const cancel = () => { setForm(blank()); setEditId(null); setSelectedTiep(null); };

  // Only show CUSDEC with balance > 0 (or the one being edited)
  const available = tiep.filter(t => {
    const bal = +t.qty - +t.issued_qty;
    if (editId && t.cusdec_no === form.cusdec_no) return true;
    return bal > 0;
  });

  const balance = selectedTiep ? +selectedTiep.qty - +selectedTiep.issued_qty : null;

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Issue Goods</div><div className="page-sub">Record export usage against TIEP imports</div></div>
      </div>
      <div className="page-body" style={{ display:'flex', flexDirection:'column', gap:18 }}>
        <div style={{ maxWidth:900 }}>
          {editId && (
            <div className="alert alert-warning" style={{ marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span>✏️ Editing record #{editId}</span>
              <button className="btn btn-secondary btn-sm" onClick={cancel}>Cancel Edit</button>
            </div>
          )}
          <div className="card">
            <div className="card-header"><span className="card-title">{editId ? '✏️ Edit Issue Record' : '↗ Issue / Export Details'}</span></div>
            <div className="card-body">
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label className="lbl">Date *</label>
                  <input className="input" type="date" value={form.date} onChange={set('date')} />
                </div>
                <div className="form-group">
                  <label className="lbl">Export CusDec *</label>
                  <input className="input" value={form.export_cusdec} onChange={set('export_cusdec')} placeholder="e.g. E 16505" />
                </div>
                <div className="form-group">
                  <label className="lbl">Client *</label>
                  <input className="input" value={form.client} onChange={set('client')} placeholder="e.g. Organic Pineapple Ltd" />
                </div>
                <div className="form-group">
                  <label className="lbl">Invoice Value (LKR)</label>
                  <input className="input" type="number" step="0.01" value={form.inv_value_usd} onChange={set('inv_value_usd')} placeholder="e.g. 26471.20" />
                </div>
                <div className="form-group">
                  <label className="lbl">Export INV No *</label>
                  <input className="input" value={form.export_inv_no} onChange={set('export_inv_no')} placeholder="e.g. WNEX22-0002" />
                </div>
                <div className="form-group">
                  <label className="lbl">TIEP CUSDEC No * (balance &gt; 0)</label>
                  <select className="input" value={form.cusdec_no} onChange={e => handleCusdec(e.target.value)}>
                    <option value="">-- Select CUSDEC --</option>
                    {available.map(t => {
                      const bal = +t.qty - +t.issued_qty;
                      return <option key={t.id} value={t.cusdec_no}>{t.cusdec_no} | {t.name} | Bal: {bal.toLocaleString()}</option>;
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="lbl">HS Code (auto)</label>
                  <input className="input" readOnly value={form.hs_code} placeholder="Select CUSDEC first" />
                </div>
                <div className="form-group">
                  <label className="lbl">Issue Quantity *</label>
                  <input className="input" type="number" value={form.quantity} onChange={e => handleQty(e.target.value)} placeholder="e.g. 5808" />
                </div>
                <div className="form-group">
                  <label className="lbl">Units (auto)</label>
                  <input className="input" readOnly value={form.units} placeholder="Auto-filled" />
                </div>
                <div className="form-group">
                  <label className="lbl">Liability LKR (auto)</label>
                  <input className="input" readOnly value={form.liability ? fmtN(form.liability) : ''} placeholder="Auto-calculated" />
                </div>
              </div>

              {selectedTiep && (
                <div style={{ marginTop:14, background:'var(--light)', borderRadius:10, padding:'12px 16px', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, fontSize:12 }}>
                  {[['Supplier',selectedTiep.name],['Total QTY',fmtN(selectedTiep.qty,0)],['Issued',fmtN(selectedTiep.issued_qty||0,0)],['Available Balance',`${fmtN(balance,0)} ${selectedTiep.units}`]].map(([l,v],i) => (
                    <div key={l}><span style={{ color:'var(--text2)', display:'block' }}>{l}</span><strong style={{ color: i===3 && balance<=0 ? 'var(--danger)' : 'var(--teal)' }}>{v}</strong></div>
                  ))}
                </div>
              )}

              <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:18, paddingTop:14, borderTop:'1px solid var(--border)' }}>
                <button className="btn btn-secondary" onClick={cancel}>Clear</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? '…' : editId ? 'Update Record' : 'Save Issue Record'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">⏱ Issue History</span></div>
          <div className="tbl-wrap">
            <table>
              <thead><tr>{['Date','Exp CusDec','Client','Inv Value','Export INV','CUSDEC','HS','QTY','Units','Liability',''].map(h=><th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {issues.length === 0
                  ? <tr><td colSpan={11} style={{ textAlign:'center', padding:24, color:'var(--text2)' }}>No issue records yet.</td></tr>
                  : issues.map(i => (
                    <tr key={i.id} style={{ background: editId===i.id ? '#fefce8' : undefined }}>
                      <td>{new Date(i.date).toLocaleDateString('en-GB')}</td>
                      <td className="mono">{i.export_cusdec}</td>
                      <td>{i.client}</td>
                      <td>{fmtN(i.inv_value_usd)}</td>
                      <td className="mono">{i.export_inv_no}</td>
                      <td className="mono">{i.cusdec_no}</td>
                      <td className="mono">{i.hs_code}</td>
                      <td>{fmtN(i.quantity,0)}</td>
                      <td>{i.units}</td>
                      <td>{fmtN(i.liability)}</td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn-icon teal" onClick={() => handleEdit(i)} title="Edit">✏️</button>
                          <button className="btn-icon danger" onClick={() => handleDelete(i.id)} title="Delete">🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
